"use client";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const toggleVariants = cva(
  "[&_svg]:-mx-0.5 relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-medium text-base text-foreground outline-none transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 data-pressed:bg-input/64 data-pressed:text-accent-foreground sm:text-sm [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    defaultVariants: {
      size: "xl",
      variant: "default",
    },
    variants: {
      size: {
        xs: "h-[var(--button-height,1.5rem)] gap-1.5 px-2 text-xs rounded-sm [&_svg:not([class*='size-'])]:size-2.5",
        sm: "h-[var(--button-height,1.75rem)] px-2.5 gap-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        md: "h-[var(--button-height,2rem)] px-3 py-2",
        lg: "h-[var(--button-height,2.25rem)] px-3.5",
        xl: "h-[var(--button-height,2.5rem)] [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-5 px-4",
        "icon-xs": "size-[var(--button-height,1.5rem)] rounded-sm",
        "icon-sm": "size-[var(--button-height,1.75rem)]",
        "icon-md": "size-[var(--button-height,2rem)]",
        "icon-lg": "size-[var(--button-height,2.25rem)]",
        "icon-xl": "size-[var(--button-height,2.5rem)] [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-4",
      },
      variant: {
        default: "border-transparent",
        outline:
          "border-input bg-background not-dark:bg-clip-padding shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] dark:bg-input/32 dark:data-pressed:bg-input dark:hover:bg-input/64 dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] dark:not-disabled:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/2%)] [:disabled,:active,[data-pressed]]:shadow-none",
      },
    },
  },
);

function Toggle({
  className,
  variant,
  size,
  ...props
}: TogglePrimitive.Props & VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive
      className={cn(toggleVariants({ className, size, variant }))}
      data-feedback="tap"
      data-slot="toggle"
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
